import React, { useContext, useMemo } from 'react';
import invariant from 'tiny-invariant';
import { ICustomer } from '@monofront/types';
import { IAuthUser } from './createAuthContext';
import { MEMBER_MALIN } from '../constants';

export const ProductCustomerContext = React.createContext<{
  isMalin: boolean;
  isMember: boolean;
  isAuthenticated: boolean;
  email: string | null;
} | null>(null);

type ProductCustomerProviderProps = {
  useAuthentication: () => {
    customer?: Pick<ICustomer, 'omsTags'> | null | undefined;
    authUser?: Pick<IAuthUser, 'email'> | null | undefined;
    isMember: boolean;
    isAuthenticated: boolean;
  };
  children: React.ReactNode;
};

export const ProductCustomerProvider = ({
  useAuthentication,
  children,
}: ProductCustomerProviderProps) => {
  const { authUser, customer, isMember, isAuthenticated } = useAuthentication();

  return (
    <ProductCustomerContext.Provider
      value={useMemo(
        () => ({
          isMalin: customer?.omsTags?.includes(MEMBER_MALIN) ?? false,
          isMember,
          isAuthenticated,
          email: authUser?.email ?? null,
        }),
        [authUser?.email, customer?.omsTags, isMember, isAuthenticated],
      )}
    >
      {children}
    </ProductCustomerContext.Provider>
  );
};

export const useProductCustomer = () => {
  const ctx = useContext(ProductCustomerContext);

  invariant(
    ctx,
    'useProductCustomer must be used within a ProductCustomerContext.Provider',
  );

  return ctx;
};
